import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface ServerRow {
  id: string;
  name: string;
  base_url: string;
  notes: string | null;
  ip_whitelist: string[];
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  created_at: string;
  clients_count: number;
  present_in_sheet: boolean;
  last_sheet_sync_at: string | null;
}

export type PresenceFilter = '' | 'in_sheet' | 'not_in_sheet';

export interface SyncConfig {
  sheet_sync_enabled: boolean;
  sheet_sync_interval_hours: number;
  last_run_at: string | null;
  last_run_success: boolean | null;
  last_run_payload: unknown;
  last_run_error: unknown;
}

export interface ServerCreateBody {
  base_url: string;
  name?: string;
  notes?: string | null;
  ip_whitelist?: string[];
}

export interface ImportError {
  row: number;
  dominio: string;
  msg: string;
}

export interface ImportResult {
  total_rows: number;
  imported_new: number;
  imported_updated: number;
  skipped: number;
  skipped_no_datacole: number;
  errors: ImportError[];
  enqueued: number;
}

export function useServers(opts?: {
  refetchInterval?: number | false;
  presence?: PresenceFilter;
}) {
  const presence = opts?.presence ?? '';
  return useQuery({
    queryKey: ['servers', presence],
    queryFn: () =>
      api<{ rows: ServerRow[] }>(`/servers${presence ? `?presence=${presence}` : ''}`),
    refetchInterval: opts?.refetchInterval ?? false,
    placeholderData: (prev) => prev,
  });
}

export function useSyncConfig() {
  return useQuery({
    queryKey: ['servers', 'sync-config'],
    queryFn: () => api<SyncConfig>('/servers/sync-config'),
    refetchInterval: 30_000,
  });
}

export function useTriggerSheetSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ enqueued: true }>('/servers/sync-sheet', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['servers'] });
    },
  });
}

export function useCreateServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ServerCreateBody) =>
      api<ServerRow>('/servers', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['servers'] }),
  });
}

export function useUpdateServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ServerCreateBody> }) =>
      api<ServerRow>(`/servers/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['servers'] }),
  });
}

export function useDeleteServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/servers/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['servers'] }),
  });
}

export function useTestServer() {
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean; message: string; status?: number; health?: unknown }>(
        `/servers/${id}/test`,
        { method: 'POST' },
      ),
  });
}

export function useImportServersFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api<ImportResult>('/servers/import-file', { method: 'POST', body: fd });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['servers'] }),
  });
}

export function useImportServersUrl() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (url: string) =>
      api<ImportResult>('/servers/import-url', { method: 'POST', body: { url } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['servers'] }),
  });
}
